import { generatePageDesignerLists } from '@/utils/page-designer-schema.server';
import { getSchemaSite } from '@/lib/schema-site.server';
import { Suspense } from 'react';
import { ContentJsonLd } from '@/components/content-json-ld';
import { generateContentSchema, resolveContentSchemaType } from '@/utils/content-schema';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import { getPublicOrigin } from '@/utils/schema-url';
import {
    redirect,
    type ActionFunctionArgs,
    type LoaderFunctionArgs,
    type ShouldRevalidateFunctionArgs,
} from 'react-router';
import { FolderListingRoute } from '@/content-routes/folder-listing';
import { ContentPillarPageRoute } from '@/content-routes/content-pillar-page';
import { LocationPageRoute } from '@/content-routes/location-page';
import { AttributeDefinition, PageType, RegionDefinition } from '@/lib/decorators';
import type {
    ContentPillarPageRouteData,
    ContentRouteData,
    LocationPageRouteData,
    PageDesignerRouteData,
} from '@/content-routes/types';
import { getCatchAllActionHandler, getCatchAllLoaderHandler } from '@/lib/auth-catch-all.server';
import { shouldSkipRevalidationForCartOrWishlistAndNewsletterActions } from '@/lib/should-revalidate';
import { fetchPageWithComponentData, type Page } from '@/lib/util/pageLoader.server';
import { buildUrlFromContext } from '@/lib/url.server';
import { fetchContentRouteIndex, normalizeContentRoutePath } from '@/lib/api/content-route-index.server';
import { fetchPageDesignerDefaultFolder } from '@/lib/api/page-designer-folder.server';
import { buildDirectPageBreadcrumbItems } from '@/content-routes/breadcrumbs';
import { resolveBreadcrumbMergeHomeWithFirstItem } from '@/lib/breadcrumb/resolve-breadcrumb-merge-home';
import { PageDesignerRoute } from '@/content-routes/page-designer';
import { getConfig } from '@salesforce/storefront-next-runtime/config';
import { readPrimaryShoppingCategoryPath } from '@/lib/primary-shopping-category.server';
import type { EatalyBreadcrumbItem } from '@/components/eataly-breadcrumbs';
import {
    loadContentFolderRoute,
    resolveEditorialPillarRoot,
} from '@/content-routes/loaders/folder-listing-loader.server';
import type { NavigationPillar } from '@/lib/navigation-pillars';
import { finalizePageDesignerRoute } from '@/content-routes/loaders/finalize-page-designer-route.server';
import { getSearchWithoutClientOnlyParams } from '@/lib/plp-panel-url-state';
import { buildPageDesignerPageIdToPathMap } from '@/lib/page-designer/resolve-component-urls-from-route-index.server';
import { notFoundResponse } from '@/lib/not-found.server';
import { attachAnalytics } from '@/lib/analytics/route-analytics';
import { preferredSettledRejection } from '@/lib/maintenance';
import {
    logMissingEditorialContentGroup,
    resolveEditorialRouteContentGroup,
    type ContentGroupValue,
} from '@/lib/analytics/content-group';
import {
    contentPillarPageAnalyticsContentGroupDefinition,
    genericContentPageAnalyticsContentGroupDefinition,
    locationPageAnalyticsContentGroupDefinition,
} from '@/lib/analytics/page-designer-content-group';
import { getLogger } from '@/lib/logger.server';
import { resolveParentLocationLabelFromBreadcrumbs } from '@/lib/page-location';

/** Shopper Experience page type id identifying a Location Page. */
const LOCATION_PAGE_TYPE_ID = 'locationPage';

/** Shopper Experience page type id identifying a Content Pillar Page. */
const CONTENT_PILLAR_PAGE_TYPE_ID = 'contentPillarPage';

/** Shopper Experience page type id identifying a News Listing Page. */
const NEWS_LISTING_PAGE_TYPE_ID = 'newsListingPage';

/** Shopper Experience page type id identifying the Eataly Club landing page. */
const EATALY_CLUB_PAGE_TYPE_ID = 'eatalyClubPage';

function resolveCriticalContentPageComponents(page: Page) {
    if (page.typeId === CONTENT_PILLAR_PAGE_TYPE_ID) {
        return [{ regionId: 'pillarNavigation' }, { regionId: 'content', limit: 2 }];
    }
    if (page.typeId === NEWS_LISTING_PAGE_TYPE_ID) {
        return [{ regionId: 'newsHighlighted' }];
    }
    if (page.typeId === EATALY_CLUB_PAGE_TYPE_ID) {
        return [{ regionId: 'content', limit: 1 }];
    }
    // Above-the-fold components only: loaders of later ones (carousels, tiles, recipes) stream instead of
    // delaying the HTML.
    return [{ regionId: 'content', limit: 2 }];
}

@PageType({
    name: 'Generic Content Page',
    description: 'Generic content page',
    supportedAspectTypes: [],
})
@RegionDefinition([
    {
        id: 'content',
        name: 'Content Region',
        description: 'Content area',
        maxComponents: 20,
        componentTypeInclusions: [
            'carousels.crossSellCarousel',
            'carousels.editorialCarousel',
            'carousels.groupedCarousel',
            'carousels.squareCardCarousel',
            'content.cardListings',
            'content.cardMultiImages',
            'content.heroText',
            'content.heroBanner',
            'content.editorialArticleAsideComponent',
            'content.editorialSeoLinkingSection',
            'content.heroBanner',
            'content.heroModules',
            'content.heroTextMedia',
            'content.inspirationHeader',
            'content.mediaFullBleed',
            'content.recommendedRecipe',
            'content.recommendedCourse',
            'content.editorialTextMedia',
            'content.seoText',
            'content.editorialTextOnly',
            'content.editorialPromoBanner',
            'content.editorialPeopleBanner',
            'content.editorialStoreInfo',
            'content.editorialLocationContactsSection',
            'layout.editorialViewportWrapper',
            'layout.editorialTabs',
            'content.editorialJobOpportunities',
            'content.editorialHeaderPress',
            'content.editorialFaqHelpHub',
            'content.editorialHelpCardRows',
            'content.editorialSupportContactForm',
            'content.editorialForm',
            'content.numbers',
        ],
    },
])
export class GenericContentPageMetadata {
    @AttributeDefinition({
        id: 'storeId',
        name: 'Store ID',
        type: 'string',
        required: false,
        description: 'The id of the store rendered on this page',
        searching: {
            searchable: true,
            refinable: true,
            boostFactor: 2,
            sortable: true,
        },
    })
    storeId?: string;
    @AttributeDefinition({
        id: 'photoInCardListing',
        name: 'Photo in Card Listing',
        type: 'image',
        required: false,
        description:
            'The photo to display in the card listing; this attribute appears in the every folder listing page',
    })
    photoInCardListing?: string;
    @AttributeDefinition({
        id: 'subtitleString',
        name: 'Subtitle String',
        type: 'string',
        required: false,
        description:
            'The subtitle string; this attribute appears in the news listing page (es Dal 7/4/2026 al 6/5/2026)',
    })
    subtitleString?: string;

    @AttributeDefinition(genericContentPageAnalyticsContentGroupDefinition)
    analyticsContentGroup!: ContentGroupValue;
}

@PageType({
    id: LOCATION_PAGE_TYPE_ID,
    name: 'Location Page',
    description: 'Store location page bound to a single store',
    supportedAspectTypes: [],
})
@RegionDefinition([
    {
        id: 'content',
        name: 'Content Region',
        description: 'Content area',
        maxComponents: 20,
        componentTypeInclusions: [
            'carousels.editorialCarousel',
            'carousels.squareCardCarousel',
            'carousels.crossSellCarousel',
            'carousels.groupedCarousel',
            'content.cardListings',
            'content.heroTextMedia',
            'content.heroText',
            'content.heroBanner',
            'content.editorialSeoLinkingSection',
            'content.editorialTextMedia',
            'content.menu',
            'content.socialLinks',
            'content.recommendedCourse',
            'content.editorialTextOnly',
            'layout.editorialTextOnly',
            'layout.editorialTabs',
            'content.editorialPeopleBanner',
        ],
    },
])
export class LocationPageMetadata {
    @AttributeDefinition({
        id: 'storeId',
        name: 'Store ID',
        type: 'string',
        required: true,
        description: 'The id of the store rendered on this location page',
        searching: {
            searchable: true,
            refinable: true,
            boostFactor: 2,
            sortable: true,
        },
    })
    storeId!: string;

    @AttributeDefinition({
        id: 'photoInCardListing',
        name: 'Photo in Card Listing',
        type: 'image',
        required: false,
        description:
            'The photo to display in the card listing; this attribute appears in the every folder listing page',
    })
    photoInCardListing?: string;

    @AttributeDefinition(locationPageAnalyticsContentGroupDefinition)
    analyticsContentGroup!: ContentGroupValue;
}

@PageType({
    id: CONTENT_PILLAR_PAGE_TYPE_ID,
    name: 'Content Pillar Page',
    description: 'Editorial pillar landing page with pillar navigation and editorial content.',
    supportedAspectTypes: [],
})
@RegionDefinition([
    {
        id: 'pillarNavigation',
        name: 'Pillar navigation',
        description: 'Horizontal pillar switcher tabs',
        maxComponents: 1,
        componentTypeInclusions: ['navigation.pillarNavigation'],
    },
    {
        id: 'content',
        name: 'Content',
        description: 'Editorial content blocks',
        maxComponents: 10,
        componentTypeInclusions: [
            'carousels.crossSellCarousel',
            'carousels.editorialCarousel',
            'carousels.groupedCarousel',
            'carousels.squareCardCarousel',
            'content.cardListings',
            'content.cardMultiImages',
            'content.heroText',
            'content.seoText',
            'content.heroBanner',
            'content.editorialArticleAsideComponent',
            'content.editorialSeoLinkingSection',
            'content.heroTextMedia',
            'content.inspirationHeader',
            'content.mediaFullBleed',
            'content.recommendedRecipe',
            'content.recommendedCourse',
            'content.editorialTextMedia',
            'content.numbers',
            'layout.editorialViewportWrapper',
        ],
    },
])
export class ContentPillarPageMetadata {
    @AttributeDefinition(contentPillarPageAnalyticsContentGroupDefinition)
    analyticsContentGroup!: ContentGroupValue;
}

@PageType({
    id: NEWS_LISTING_PAGE_TYPE_ID,
    name: 'News Listing Page',
    description: 'News list page',
    supportedAspectTypes: [],
})
@RegionDefinition([
    {
        id: 'newsHighlighted',
        name: 'Highlighted News Region',
        description: 'Highlighted news area with editorial carousel',
        maxComponents: 1,
        componentTypeInclusions: ['carousels.editorialCarousel'],
    },
    {
        id: 'pressOffice',
        name: 'Press Office Region',
        description: 'Press office area with editorial text media',
        maxComponents: 1,
        componentTypeInclusions: ['content.editorialTextMedia'],
    },
])
export class NewsListingPageMetadata {}

@PageType({
    id: EATALY_CLUB_PAGE_TYPE_ID,
    name: 'Eataly Club Page',
    description: 'Eataly Club landing page. Editorial content comes from Payload CMS; FAQs come from Zendesk.',
    supportedAspectTypes: [],
})
@RegionDefinition([
    {
        id: 'content',
        name: 'Content Region',
        description: 'Eataly Club homepage and FAQ.',
        maxComponents: 1,
        componentTypeInclusions: ['content.eatalyClubPage'],
    },
])
export class EatalyClubPageMetadata {}

export async function loader(args: LoaderFunctionArgs): Promise<ContentRouteData | Response> {
    const normalizedPath = getNormalizedContentPath(args);
    const authHandler = getCatchAllLoaderHandler(normalizedPath, args.context);
    if (authHandler) {
        return authHandler(args);
    }

    const primaryCategoryPath = await readPrimaryShoppingCategoryPath(args.request, args.context);

    const pageId = extractPageDesignerPageId(normalizedPath);
    const data = pageId
        ? await loadDirectPageDesignerRoute(args, pageId, normalizedPath, primaryCategoryPath)
        : await loadContentFolderRoute(args, normalizedPath, primaryCategoryPath);
    if (data instanceof Response) return data;
    const requestUrl = new URL(args.request.url);
    const pageUrl = buildCanonicalUrl(getPublicOrigin(args.request), requestUrl.pathname, requestUrl.search);
    const breadcrumbItems = data.breadcrumbItems.map((item) => ({
        ...item,
        href: item.href && item.href !== '#' ? buildUrlFromContext(item.href, args.context) : item.href,
    }));
    const schemaSite = getSchemaSite(args);
    const listing = data.kind === 'folderListing' || data.kind === 'newsListing';
    const structuredData = listing
        ? data.nonCriticalSearchResult.then((result) =>
              generateContentSchema({
                  type: resolveContentSchemaType(data.kind),
                  title: data.folder.title,
                  description: data.folder.description,
                  pageUrl,
                  breadcrumbItems,
                  site: schemaSite,
                  searchResult: {
                      ...result,
                      offset: data.criticalSearchResult.offset,
                      total: data.criticalSearchResult.total,
                      hits: [...data.criticalSearchResult.hits, ...result.hits].map((hit) => ({
                          ...hit,
                          path: buildUrlFromContext(hit.path, args.context),
                      })),
                  },
              })
          )
        : generatePageDesignerLists(
              data.kind === 'contentPillarPage'
                  ? {
                        ...data.page,
                        regions: data.page.regions?.filter((region) =>
                            ['pillarNavigation', 'content'].includes(region.id)
                        ),
                    }
                  : data.page,
              pageUrl,
              schemaSite,
              (path) => buildUrlFromContext(path, args.context)
          ).then((lists) =>
              generateContentSchema({
                  type: resolveContentSchemaType(data.kind, data.page),
                  lists,
                  page: data.page,
                  title: data.page.name || data.pageId,
                  description: data.page.description || data.page.pageDescription,
                  pageUrl,
                  breadcrumbItems,
                  site: schemaSite,
              })
          );
    return {
        ...data,
        structuredData: structuredData.catch((error: unknown) => {
            getLogger(args.context).error('Content: failed to generate structured data', { error });
            return null;
        }),
    };
}

export function shouldRevalidate(args: ShouldRevalidateFunctionArgs) {
    if (shouldSkipRevalidationForCartOrWishlistAndNewsletterActions(args)) {
        return false;
    }

    const { currentUrl, nextUrl, defaultShouldRevalidate } = args;

    // Revalidate when navigating between editorial/content pages on this splat route.
    if (currentUrl.pathname !== nextUrl.pathname) {
        return true;
    }

    const clientOnlyParamsChanged =
        currentUrl.search !== nextUrl.search &&
        getSearchWithoutClientOnlyParams(currentUrl.search) === getSearchWithoutClientOnlyParams(nextUrl.search);

    if (clientOnlyParamsChanged) {
        return false;
    }
    if (args.formAction?.includes('/action/support-ticket') || args.formAction?.includes('/action/form-submission')) {
        return false;
    }

    return defaultShouldRevalidate;
}

function getNormalizedContentPath(args: LoaderFunctionArgs) {
    const config = getConfig(args.context);
    const requestUrl = new URL(args.request.url);

    return normalizeContentRoutePath(requestUrl.pathname, config);
}

async function loadDirectPageDesignerRoute(
    args: LoaderFunctionArgs,
    pageId: string,
    normalizedPath: string,
    primaryCategoryPath: string | null
): Promise<PageDesignerRouteData | LocationPageRouteData | ContentPillarPageRouteData | Response> {
    const [pageResult, indexResult, defaultFolderResult] = await Promise.allSettled([
        fetchPageWithComponentData(args, { pageId }, { criticalComponents: resolveCriticalContentPageComponents }),
        fetchContentRouteIndex(args.context),
        fetchPageDesignerDefaultFolder(args.context, pageId),
    ]);
    const settledRejections = [pageResult, indexResult, defaultFolderResult].filter(
        (result): result is PromiseRejectedResult => result.status === 'rejected'
    );
    if (settledRejections.length > 0) {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw preferredSettledRejection(settledRejections);
    }
    const page = pageResult.status === 'fulfilled' ? pageResult.value : null;
    const index = indexResult.status === 'fulfilled' ? indexResult.value : undefined;
    const defaultFolder = defaultFolderResult.status === 'fulfilled' ? defaultFolderResult.value : null;

    if (!page || !defaultFolder) {
        throw await notFoundResponse(args);
    }

    const config = getConfig(args.context);
    const pathFromRouteIndex = index ? buildPageDesignerPageIdToPathMap(index).get(pageId) : undefined;
    const defaultFolderPageUrl = normalizeContentRoutePath(pathFromRouteIndex ?? defaultFolder.pageUrl, config);
    const willRedirect = defaultFolderPageUrl !== normalizedPath;
    if (willRedirect) {
        const requestUrl = new URL(args.request.url);
        return redirect(`${buildUrlFromContext(defaultFolderPageUrl, args.context)}${requestUrl.search}`);
    }
    const breadcrumbItems = buildDirectPageBreadcrumbItems(page, pageId, index, defaultFolder);
    const folderPath = defaultFolder.folder?.path;
    const folderEntry = folderPath && index ? index.routesByPath[folderPath] : undefined;
    const primaryNavigationPillar =
        defaultFolder.folder?.c_primaryNavigationPillar ?? folderEntry?.c_primaryNavigationPillar;
    const pillarRoot =
        folderEntry && primaryNavigationPillar
            ? await resolveEditorialPillarRoot(args.context, folderEntry, index)
            : null;

    return buildPageDesignerRouteData({
        args,
        page,
        pageId,
        pageUrl: defaultFolderPageUrl,
        breadcrumbItems,
        navigationPillarPathFromCookie: primaryCategoryPath,
        pillarPathToPersist: pillarRoot?.path ?? null,
        navigationPillar: pillarRoot?.navigationPillar,
        showHome: Boolean(primaryNavigationPillar),
    });
}
type BuildPageDesignerRouteDataArgs = {
    args: LoaderFunctionArgs;
    page: PageDesignerRouteData['page'];
    pageId: string;
    pageUrl: string;
    breadcrumbItems: EatalyBreadcrumbItem[];
    navigationPillarPathFromCookie: string | null;
    pillarPathToPersist: string | null;
    navigationPillar: NavigationPillar | undefined;
    showHome: boolean;
};

function buildPageDesignerRouteData({
    args,
    page,
    pageId,
    pageUrl,
    breadcrumbItems,
    navigationPillarPathFromCookie,
    pillarPathToPersist,
    navigationPillar,
    showHome,
}: BuildPageDesignerRouteDataArgs): PageDesignerRouteData | LocationPageRouteData | ContentPillarPageRouteData {
    // The cookie still holds the previous section until the client persists this route's shopping context.
    const primaryCategoryPath = pillarPathToPersist ?? navigationPillarPathFromCookie;
    const breadcrumbHomeTo = primaryCategoryPath ?? undefined;

    const routeData = finalizePageDesignerRoute(args, {
        kind: 'pageDesigner',
        page,
        pageId,
        pageUrl,
        breadcrumbItems,
        breadcrumbBackgroundClass: 'bg-neutral-white',
        breadcrumbHomeTo,
        primaryCategoryPath,
        shoppingContext: {
            primaryCategoryPath: pillarPathToPersist,
            navigationPillar,
        },
        breadcrumbMergeHomeWithFirstItem: resolveBreadcrumbMergeHomeWithFirstItem({
            items: breadcrumbItems,
            homeTo: breadcrumbHomeTo,
            currentPathname: new URL(args.request.url).pathname,
            showHome,
        }),
    });

    const logger = getLogger(args.context);

    const parentLocationLabel = resolveParentLocationLabelFromBreadcrumbs(breadcrumbItems) ?? null;

    return attachAnalytics(
        {
            ...routeData,
            parentLocationLabel,
        },
        resolveEditorialRouteContentGroup({
            page,
            kind: routeData.kind,
            pageUrl: routeData.pageUrl,
            onMissingAttribute: (details) => logMissingEditorialContentGroup(logger, details),
        })
    );
}

function extractPageDesignerPageId(path: string): string | null {
    if (!path.endsWith('.html')) {
        return null;
    }

    const slashIndex = path.lastIndexOf('/');
    const fileName = path.slice(slashIndex + 1);
    const pageId = fileName.slice(0, -'.html'.length);
    if (!pageId) {
        return null;
    }

    try {
        return decodeURIComponent(pageId);
    } catch {
        return pageId;
    }
}

export async function action(args: ActionFunctionArgs) {
    const normalizedPath = getNormalizedContentPath(args);
    const authHandler = getCatchAllActionHandler(normalizedPath, args.context);

    if (authHandler) {
        return authHandler(args);
    }

    throw new Response('Method Not Allowed', { status: 405 });
}

function ContentRouteView({ loaderData }: { loaderData: ContentRouteData }) {
    switch (loaderData.kind) {
        case 'folderListing':
            return <FolderListingRoute data={loaderData} kind="folderListing" />;
        case 'pageDesigner':
            return <PageDesignerRoute data={loaderData} />;
        case 'locationPage':
            return <LocationPageRoute data={loaderData} />;
        case 'contentPillarPage':
            return <ContentPillarPageRoute data={loaderData} />;
        case 'newsListing':
            return <FolderListingRoute data={loaderData} page={loaderData.page} kind="newsListing" />;
    }
}

export default function ContentCatchAllRoute({ loaderData }: { loaderData: ContentRouteData }) {
    return (
        <>
            <ContentRouteView loaderData={loaderData} />
            {loaderData.structuredData && (
                <Suspense fallback={null}>
                    <ContentJsonLd promise={loaderData.structuredData} />
                </Suspense>
            )}
        </>
    );
}
